<template>
    <div>
        <form-wrapper>
            <form-text-input :caption="$t('name')" v-model="internalValue.name"/>

            <form-select-input :caption="$t('comparison.operator')"
                               :options="tabOperatorOptions"
                               v-model="internalValue.operator"/>

            <form-select-input :caption="$t('comparison.operationDefinition')"
                               :options="operationDefinitionOptions"
                               v-model="internalValue.operationDefinitionId"/>

            <form-select-input :caption="$t('parameter')"
                               :options="parametersOptions"
                               v-model="internalValue.attribute"/>
        </form-wrapper>

        <template v-if="internalValue.operationDefinitionId">
            <framed-content v-for="(filter, i) in internalValue.filters"
                            :title="$t('comparison.filterLabel', {index : ''})"
                            :key="i">
                <form-wrapper>
                    <form-select-input :caption="$t('comparison.operator')"
                                       :options="searchQueryOperatorOptions"
                                       v-model="filter.operator"/>
                </form-wrapper>

                <filter-form :filter-definition="filterDefinition" v-model="filter.attributes"
                             :key="i*i"/>

                <simple-layout horizontal>
                    <div/>
                    <div/>
                    <div/>
                    <div/>
                    <simple-button @press="handleRemoveFilter(i)" :disabled="!internalValue.operationDefinitionId">
                        {{ $t('comparison.removeFilter') }}
                    </simple-button>
                </simple-layout>
            </framed-content>
        </template>

        <simple-layout horizontal>
            <div/>
            <div/>
            <div/>
            <div/>
            <simple-button @press="handleAddNewFilter" :disabled="!internalValue.operationDefinitionId">
                {{ $t('comparison.addNewSearchQuery') }}
            </simple-button>
        </simple-layout>


        <framed-content v-if="operationsList.length"
                        :title="$t('comparison.list')" :default-collapsed="true" class="width-class">
            <data-table
              :key="internalValue.operationDefinitionId"
              :data="operationsList"
              :configuration="operationsTableConfig"
              :component-id="`comparison-operations[${internalValue.operationDefinitionId}]-list`"
            />
        </framed-content>

    </div>
</template>

<script>
import FormTextInput from "@/components/form/FormTextInput";
import FormWrapper from "@/components/form/FormWrapper";
import { mapGetters, mapState } from "vuex";
import FormSelectInput from "@/components/form/FormSelectInput";
import FramedContent from "@/components/layout/FramedContent";
import QueryUtils from "@/utils/QueryUtils";
import FilterForm from "@/components/filters/FilterForm";
import DataTable from "@/components/table/DataTable";
import SimpleLayout from "@/components/layout/SimpleLayout";
import SimpleButton from "@/components/button/SimpleButton";
import Axios from "axios";
import utils from "@/utils/utils";


const defaultValue = par => d => d.content[par.name];
const styleLambdas = {
    STRING: undefined,
    DATE: undefined,
    DECIMAL: () => ({'text-align': 'right'}),
    CURRENCY: undefined,
};

const PAGE_SIZE = 100;
const URL = 'operation/api/v1/operations';


export default {
    name: "ComparisonTab",
    components: {
        SimpleButton,
        SimpleLayout,
        DataTable,
        FilterForm,
        FramedContent,
        FormSelectInput,
        FormWrapper,
        FormTextInput
    },
    props: {
        value: {
            type: Object,
            default: () => ({})
        },
    },
    data() {
        return {
            internalValue: {},
            operationsList: [],
            calculatedResult: '',

            tabOperatorOptions: [
                {
                    id: '+',
                    name: '+'
                },
                {
                    id: '-',
                    name: '-'
                }
            ],

            searchQueryOperatorOptions: [
                {
                    id: 'ADD',
                    name: 'Přidat množinu'
                },
                {
                    id: 'REMOVE',
                    name: 'Odebrat množinu'
                },

            ],
            operationsTableConfig: {columns: []},
            cachedOperations: {},

            valueLambdas: {
                STRING: defaultValue,
                ENUM: par => d => {
                    let value = d.content[par.name];
                    let enumOption = par.details.enumValues.find(op => op.value === value);
                    if (enumOption === undefined) return value;
                    else return enumOption.name;
                },
                DATE: par => d => this.$formats.formatDate(defaultValue(par)(d), false),
                DECIMAL: par => d => this.$numeral(d.content[par.name] / Math.pow(10, par.details.precision)).format('0,0.' + '0'.repeat(par.details.precision)),
                INTEGER: par => d => this.$numeral(d.content[par.name]).format('0'),
                BOOLEAN: par => d => d.content[par.name] ? this.$t("yes") : this.$t("no"),
                GEO_POINT: par => d => {
                    if (!d.content[par.name]) {
                        return "N/A"
                    }
                    return `${this.$formats.formatAmount(d.content[par.name]?.longtitude)}/${this.$formats.formatAmount(d.content[par.name]?.latitude)}`;
                },
                ENTITY_REFERENCE: par => d => {
                    let ref_id = d.content[par.name];
                    if (par.details.entityType === 'account') {
                        let acc = this.account(ref_id) ?? "N/A";
                        return acc.name
                    }
                    if (par.details.entityType === 'party') {
                        let party = this.party(ref_id) ?? "N/A";
                        return party.displayName
                    }
                    if (par.details.entityType === 'operation') {
                        return this.cachedOperations[ref_id] ?? "N/A"
                    }
                    return ref_id;
                },
                DYNA_MODEL: () => () => "Complex Object {...}",
                ARRAY: par => d => `${d.content[par.name].length} items`
            }
        }
    },
    computed: {
        ...mapGetters({
            operationDefinitionOptions: "operationDefinitions/operationDefinitions",
            getOperationDefinition: "operationDefinitions/operationDefinition",
            currencies: "currencies/currencies",
            accounts: "banking/accounts",
            parties: "parties/parties",
            users: "users/users",

            party: "parties/party",
            account: "banking/account",
        }),

        ...mapState('comparison', {
            // internalValue: "selectedTab",
        }),


        currencyOptions() {
            return this.currencies.map(ccy => ({id: ccy.code, name: ccy.code}));
        },
        accountOptions() {
            return this.accounts.map(acc => ({id: acc.id, name: acc.name}))
        },
        partyOptions() {
            return this.parties.map(pty => ({id: pty.id, name: pty.displayName == null ? "N/A" : pty.displayName}))
        },
        userOptions() {
            return this.users.map(usr => ({id: usr.id, name: usr.username}))
        },

        operationDefinition() {
            return this.getOperationDefinition(this.internalValue.operationDefinitionId);
        },

        parametersOptions() {
            const isNumberAttribute = attribute => attribute.details.type === 'DECIMAL';

            if (this.operationDefinition) {
                const dynaProperties = Object.values(this.operationDefinition.model.dynaProperties);

                return dynaProperties
                  .filter(isNumberAttribute)
                  .map(attribute => ({
                      id: attribute.name,
                      name: attribute.displayName
                  }))
            }

            return []
        },

        filterDefinition() {
            if (this.operationDefinition) {
                return QueryUtils.OperationQueryDefinition(this.operationDefinition, {
                    accounts: this.accountOptions,
                    currencies: this.currencyOptions,
                    parties: this.partyOptions,
                    users: this.userOptions
                });
            }
            return {};
        },
    },
    methods: {
        createElementaryColumns() {
            this.operationsTableConfig.columns = [
                {
                    caption: this.$t('status'),
                    datum: d => d.actualStatus,
                    style: () => undefined
                },
                {
                    caption: "Id",
                    datum: d => d.id.substr(d.id.length - 5),
                    style: () => undefined
                }
            ]
        },

        handleRemoveFilter(index) {
            this.internalValue.filters.splice(index, 1)
        },

        handleAddNewFilter() {
            this.internalValue.filters.push({
                attributes: {
                    parameters: {},
                },
                operator: 'ADD'
            })
        },

        async getOperationsBySearchQuery(searchQuery, pageNumber, result) {
            const searchQueryUri = encodeURIComponent(JSON.stringify(searchQuery));

            const response = await Axios.get(`${URL}?size=${PAGE_SIZE}&page=${pageNumber}&query=${searchQueryUri}`);
            const responseData = await utils.parsePageResponse(response);

            const nextPageNumber = pageNumber + 1;

            result.push(...responseData.list)

            if (nextPageNumber < responseData.meta.pageCount) {
                await this.getOperationsBySearchQuery(searchQuery, nextPageNumber, result);
            }

            return result;
        },


        async getOperations() {

            const getOperationsWithOperator = async (filter) => {

                const operations = [];

                filter.attributes.parameters.operationDefinition = {
                    operator: "eq",
                    value: this.internalValue.operationDefinitionId,
                    type: "string",
                    negation: false
                };

                await this.getOperationsBySearchQuery(filter.attributes, 0, operations);

                return {
                    operator: filter.operator,
                    operations
                }
            };


            const applyOperators = (list, operationsWithOperator) => {

                for (const actualOperation of operationsWithOperator.operations) {

                    const operationIndexInlList = list.findIndex(operation => operation.id === actualOperation.id);

                    if (operationsWithOperator.operator === 'ADD' && operationIndexInlList === -1) {
                        list.push(actualOperation)
                    } else if (operationsWithOperator.operator === 'REMOVE' && operationIndexInlList !== -1) {
                        list.splice(operationIndexInlList, 1);
                    }
                }

                return list;
            }


            const operationsWithOperator = await Promise.all(this.internalValue.filters.map(getOperationsWithOperator));

            return operationsWithOperator.reduce(applyOperators, []);

        },

        async calculateResult() {

            if (!this.internalValue.filters.length) {
                this.$notify({
                    group: 'application',
                    title: this.$t('comparison.calculateError'),
                    text: this.$t('comparison.noFilters', {cardName: this.internalValue.name}),
                    type: "error"
                });
            }

            try {
                const precision = this.operationDefinition.model.dynaProperties[this.internalValue.attribute].details.precision;

                this.operationsList = await this.getOperations();

                const result = this.operationsList
                  .map(operation => operation.content[this.internalValue.attribute])
                  .reduce((prev, cur) => prev + cur, 0);

                this.calculatedResult = this.$numeral(result / (10 ** precision)).format('0,0.' + '0'.repeat(precision));
                return this.calculatedResult;

            } catch (error) {
                console.warn(error)
                let message = this.$t('comparison.calculateError');
                if (!this.internalValue.attribute) {
                    message = this.$t('comparison.operationNotSelected', {cardName: this.internalValue.name})
                } else if (!this.operationDefinition) {
                    message = this.$t('comparison.attributeNotSelected', {cardName: this.internalValue.name})
                }

                this.$notify({
                    group: 'application',
                    title: this.$t('comparison.calculateError'),
                    text: message,
                    type: "error"
                });
            }

        }
    },
    watch: {
        value: {
            handler(newValue) {
                // console.log('WATCH - VALLUE', newValue)
                if (newValue && Object.keys(newValue))
                    this.internalValue = newValue;
            },
            immediate: true,
            deep: true

        },
        internalValue: {
            handler(newValue, oldValue) {
                // console.log('WATCH - INTERNAL_VALUE', newValue)
                this.$emit('input', newValue);

                if (newValue['attribute'] && oldValue['attribute']) this.$emit('tab-changed');

                if (newValue['filters'].length !== oldValue['attribute'].length) this.$emit('tab-changed');

            },
            deep: true
        },

        'internalValue.operationDefinitionId': {
            handler(newValue, oldValue) {

                if (newValue && oldValue) {
                    this.internalValue.filters = [];
                    this.operationsList = [];
                    this.$emit('tab-changed');
                }

                if (this.operationDefinition) {
                    const operationCache = {};
                    this.createElementaryColumns()

                    Object.values(this.operationDefinition.model.dynaProperties)
                      .forEach(async par => {
                          if (par.details.type === 'ENTITY_REFERENCE') {
                              const data = await this.$store.dispatch("operations/fetchOperations", {
                                  requestedPage: 0,
                                  query: par.details.entityFilter,
                                  sort: undefined,
                                  size: 100000
                              })
                              data.list.forEach(op => operationCache[op.id] = op.name ?? op.id)
                          }


                          this.operationsTableConfig.columns.push({
                              caption: par.displayName,
                              systemName: par.name,
                              datum: this.valueLambdas[par.details.type](par),
                              style: d => styleLambdas[par.details.type] ?? d,
                              sortField: `content.values.${par.name}`,
                              allowSort: true
                          })
                      })

                    this.$set(this, 'cachedOperations', operationCache);
                }

            },
            immediate: true,
        }
    }
}
</script>

<style scoped>
.width-class {
    width: calc(100vw - 435px);
}
</style>